/**
 * PriceDisplay Component
 * Extracted from DeviceSpecifications.tsx
 * Handles price display UI (loading, error, on request, normal price)
 */

import React from 'react';
import styled, { keyframes } from 'styled-components';

// Animations
const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const loadingDot = keyframes`
  0%, 80%, 100% { transform: scale(.8); opacity: .5 }
  40% { transform: scale(1); opacity: 1 }
`;

// Styled Components
const PriceDisplayContainer = styled.div`
  text-align: center;
  padding: 1.25rem 1.5rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 60px;

  .label {
    font-size: 0.65rem;
    color: #94a3b8;
    margin-bottom: 0.375rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 700;
  }

  .dark & {
    background: linear-gradient(135deg, rgba(15, 23, 42, 0.9) 0%, rgba(12, 14, 20, 0.85) 100%);
    border: 1px solid #262b36;
  }
  @media (max-width: 480px) {
    padding: 0.9rem 1rem;
    min-height: 56px;
    border-radius: 14px;
    .label { font-size: 0.6rem; margin-bottom: 0.28rem; }
  }
`;

const ShimmerBar = styled.div<{ width?: string; height?: string }>`
  width: ${p => p.width || '60%'};
  height: ${p => p.height || '36px'};
  border-radius: 10px;
  margin: 0 auto;
  background: linear-gradient(90deg, #e5ebf3 25%, #f3f6fb 50%, #e5ebf3 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;
`;

const LoadingDots = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #94a3b8;
    animation: ${loadingDot} 1.2s infinite ease-in-out;
  }
  .dot:nth-child(2) { animation-delay: .15s; }
  .dot:nth-child(3) { animation-delay: .3s; }
`;

const PriceValue = styled.div<{ $isString?: boolean }>`
  font-weight: ${({ $isString }) => ($isString ? 700 : 800)};
  color: #0f172a;
  line-height: 1.1;
  font-size: ${({ $isString }) => ($isString ? '0.72rem' : '2.0rem')};
  letter-spacing: ${({ $isString }) => ($isString ? '-0.0015em' : '-0.02em')};
  white-space: nowrap;
  word-break: keep-all;
  display: inline-block;
  max-width: 100%;

  .dark & { color: #ffffff; }
  @media (max-width: 480px) {
    font-size: ${({ $isString }) => ($isString ? '0.62rem' : '1.6rem')};
  }
`;

const ErrorMessage = styled.div`
  font-size: 0.75rem;
  color: #ef4444;
  margin-top: 0.5rem;
`;

const RetryButton = styled.button`
  margin-top: 0.5rem;
  padding: 0.4rem 0.8rem;
  font-size: 0.7rem;
  font-weight: 600;
  color: #3b82f6;
  background: transparent;
  border: 1px solid #3b82f6;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #3b82f6;
    color: white;
  }

  .dark & {
    color: #60a5fa;
    border-color: #60a5fa;
    &:hover {
      background: #60a5fa;
      color: #0f172a;
    }
  }
`;

// Helper to get user-friendly error message
const getErrorMessage = (errorCode: string | null, t: (key: string) => string): string => {
  if (!errorCode) return '';

  switch (errorCode) {
    case 'RATE_LIMITED':
      return t('rateLimitError') || 'Te veel verzoeken. Wacht even en probeer opnieuw.';
    case 'NOT_FOUND':
    case 'not_found':
      return t('priceNotFound') || 'Prijs niet beschikbaar voor deze configuratie.';
    case 'NETWORK_ERROR':
      return t('networkError') || 'Netwerkfout. Controleer je verbinding.';
    case 'pricing_failed':
    default:
      return t('pricingError') || 'Kon prijs niet berekenen.';
  }
};

// Component Props
export interface PriceDisplayProps {
  price: number | null;
  isPricing: boolean;
  priceError: string | null;
  isOnRequest?: boolean; // Show "Op aanvraag" badge/text
  t: (key: string) => string;
  onRetry?: () => void;
}

// Main Component
const PriceDisplay: React.FC<PriceDisplayProps> = ({
  price,
  isPricing,
  priceError,
  isOnRequest = false,
  t,
  onRetry,
}) => {
  const isRateLimited = priceError === 'RATE_LIMITED';

  // Format price display - show "Prijs op aanvraag" when on_request flag is set or price is 0
  const formatPrice = () => {
    const onRequestLabel = t('priceOnRequest') || t('onRequest') || 'Prijs op aanvraag';
    if (isOnRequest || price === 0) {
      return onRequestLabel;
    }
    if (typeof price === 'number' && price > 0) {
      return `€${price.toLocaleString('nl-NL')},-`;
    }
    return onRequestLabel;
  };

  return (
    <PriceDisplayContainer>
      <div className="label">{t('estimation')}</div>
      {isPricing ? (
        <div>
          <ShimmerBar width="70%" height="34px" />
          <LoadingDots>
            <span className="dot" />
            <span className="dot" />
            <span className="dot" />
          </LoadingDots>
        </div>
      ) : (
        <>
          {priceError && (
            <>
              <ErrorMessage>
                {getErrorMessage(priceError, t)}
              </ErrorMessage>
              {isRateLimited && onRetry && (
                <RetryButton onClick={onRetry}>
                  {t('retry') || 'Opnieuw proberen'}
                </RetryButton>
              )}
            </>
          )}
          {!priceError && (
            <PriceValue $isString={isOnRequest || price === 0 || typeof price !== 'number'}>
              {formatPrice()}
            </PriceValue>
          )}
        </>
      )}
    </PriceDisplayContainer>
  );
};

export default PriceDisplay;

